import React, { useState, useEffect } from 'react';
import { submitRenovationLead } from '../utils/submitRenovationLead';
import './ServiceFinderModal.css';

const DISTRICTS = ['Limassol', 'Nicosia', 'Paphos', 'Larnaca', 'Famagusta'];

const CATEGORY_LABELS = {
  renovator:    'Renovator',
  electrician:  'Electrician',
  plumber:      'Plumber',
  tiler:        'Tiler',
  painter:      'Painter',
  ac_installer: 'AC Installer',
};

const IcClose = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IcCheck = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

export default function ServiceFinderModal({ category, district: prefilledDistrict, user, sourceEstimate, onClose, categoryIcon }) {
  const label = CATEGORY_LABELS[category] || category;

  const [form, setForm] = useState({
    name: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
    email: user?.email || '',
    phone: '',
    district: prefilledDistrict || '',
    details: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const setField = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Full name is required';
    if (!form.email.includes('@')) e.email = 'Valid email required';
    if (!form.phone.trim()) e.phone = 'Phone number is required';
    if (!form.district) e.district = 'District is required';
    if (!form.details.trim()) e.details = 'Project details are required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitRenovationLead({
        category,
        customerName: form.name.trim(),
        customerEmail: form.email.trim(),
        customerPhone: form.phone.trim(),
        district: form.district,
        projectDetails: form.details.trim(),
        timeline: null,
        budgetRange: null,
        userId: user?.id || null,
        sourceEstimate: sourceEstimate || null,
      });
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setErrors({ submit: 'Something went wrong. Please try again.' });
    }
    setSubmitting(false);
  };

  return (
    <div className="sfm-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sfm-modal">
        {success ? (
          <div className="sfm-success">
            <div className="sfm-success__icon"><IcCheck /></div>
            <div className="sfm-success__title">Request received</div>
            <div className="sfm-success__msg">
              We'll connect you with verified {label.toLowerCase()}s in {form.district} within 24 hours.
            </div>
            <button className="sfm-success__done" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <div className="sfm-header">
              {categoryIcon && (
                <div className="sfm-header__icon">{categoryIcon}</div>
              )}
              <div className="sfm-header__text">
                <div className="sfm-header__title">Find {label}</div>
                <div className="sfm-header__sub">Tell us what you need — we'll match you with verified professionals</div>
              </div>
              <button className="sfm-header__close" onClick={onClose} aria-label="Close"><IcClose /></button>
            </div>

            <form className="sfm-body" onSubmit={handleSubmit} noValidate>
              <div className="sfm-field">
                <label className="sfm-label">Full Name <span>*</span></label>
                <input
                  className={`sfm-input${errors.name ? ' error' : ''}`}
                  type="text"
                  placeholder="Your full name"
                  value={form.name}
                  onChange={e => setField('name', e.target.value)}
                />
                {errors.name && <span className="sfm-err">{errors.name}</span>}
              </div>

              <div className="sfm-row">
                <div className="sfm-field">
                  <label className="sfm-label">Email <span>*</span></label>
                  <input
                    className={`sfm-input${errors.email ? ' error' : ''}`}
                    type="email"
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={e => setField('email', e.target.value)}
                  />
                  {errors.email && <span className="sfm-err">{errors.email}</span>}
                </div>
                <div className="sfm-field">
                  <label className="sfm-label">Phone <span>*</span></label>
                  <input
                    className={`sfm-input${errors.phone ? ' error' : ''}`}
                    type="tel"
                    placeholder="+357 99 000 000"
                    value={form.phone}
                    onChange={e => setField('phone', e.target.value)}
                  />
                  {errors.phone && <span className="sfm-err">{errors.phone}</span>}
                </div>
              </div>

              <div className="sfm-field">
                <label className="sfm-label">District <span>*</span></label>
                <select
                  className={`sfm-select${errors.district ? ' error' : ''}`}
                  value={form.district}
                  onChange={e => setField('district', e.target.value)}
                >
                  <option value="">Select district…</option>
                  {DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                {errors.district && <span className="sfm-err">{errors.district}</span>}
              </div>

              <div className="sfm-field">
                <label className="sfm-label">Project Details <span>*</span></label>
                <textarea
                  className={`sfm-textarea${errors.details ? ' error' : ''}`}
                  placeholder="Describe what you need done. Include rough size, urgency, and any special requirements."
                  value={form.details}
                  onChange={e => setField('details', e.target.value)}
                />
                {errors.details && <span className="sfm-err">{errors.details}</span>}
              </div>

              {errors.submit && <span className="sfm-err">{errors.submit}</span>}

              <button className="sfm-submit" type="submit" disabled={submitting}>
                {submitting ? 'Submitting…' : `Send Request`}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
